<template>
  <sticky-header-layout>
    <template #header>
      <div class="action-container">
        <span>
          <el-button
            class="cancel-button"
            icon="el-icon-close"
            size="mini"
            type="primary"
            @click="handleCancel"
          >{{ $t('button.back') }}</el-button>
        </span>
      </div>
    </template>

    <div v-loading="isLoading">
      <el-form
        ref="form"
        :model="form"
        :rules="formRules"
        class="round-border-form"
        label-position="right"
        label-width="140px"
      >
        <div class="basic-info-container">
          <h4 class="title">{{ $t('table.basicInfo') }}</h4>
          <el-form-item
            :label="$t('table.title')"
            prop="title"
          >
            <el-input
              v-model="form.title"
              clearable
              readonly
            />
          </el-form-item>
          <el-form-item :label="$t('table.enable')">
            <el-switch
              v-model="form.enabled"
              readonly
            />
          </el-form-item>
          <el-form-item :label="$t('table.list')">
            <el-switch
              v-model="form.listed"
              readonly
            />
          </el-form-item>
          <el-form-item :label="$t('table.date')">
            <el-date-picker
              v-model="form.created_at"
              :clearable="false"
              :placeholder="$t('placeholder.pickDay')"
              type="date"
              value-format="yyyy-MM-dd"
              readonly
            />
          </el-form-item>
          <el-form-item :label="$t('table.category')">
            <el-select
              v-model="form.category_id"
              :placeholder="$t('placeholder.select')"
              readonly
            >
              <el-option
                v-for="category in categories"
                :key="category.value"
                :label="category.label"
                :value="category.value"
              />
            </el-select>
          </el-form-item>
          <el-form-item :label="$t('table.locations')">
            <el-select
              v-model="form.location"
              :placeholder="$t('placeholder.select')"
              readonly
            >
              <el-option
                v-for="location in locations"
                :key="location.value"
                :label="location.label"
                :value="location.value"
              />
            </el-select>
          </el-form-item>
          <el-form-item :label="$t('table.gender')">
            <el-select
              v-model="form.gender"
              :placeholder="$t('placeholder.select')"
              readonly
            >
              <el-option
                v-for="gender in genders"
                :key="gender.value"
                :label="gender.label"
                :value="gender.value"
              />
            </el-select>
          </el-form-item>
          <el-form-item
            :label="$t('table.previewToken')"
            prop="previewToken"
          >
            <el-input
              v-model.trim="form.preview_token"
              clearable
              readonly
            />
          </el-form-item>
          <el-form-item
            :label="$t('table.photoCredit')"
            prop="photoCredit"
          >
            <el-input
              v-model="form.photo_credit"
              clearable
              readonly
            />
          </el-form-item>
          <el-form-item
            label="U1"
            prop="u1"
          >
            <el-input
              v-model.trim="form.u1"
              clearable
              readonly
            />
          </el-form-item>
          <el-form-item
            label=""
            prop=""
          >
            <el-checkbox
              v-model="form.is_replace_with_same_store"
              readonly
            >
              {{ $t('table.sameStoreSimilarProducts') }}
            </el-checkbox>
            <el-checkbox
              v-model="form.is_replace_with_same_product"
              readonly
            >
              {{ $t('table.sameProductDifferentStore') }}
            </el-checkbox>
            <el-checkbox
              v-model="form.is_replace_with_same_brand"
              readonly
            >
              {{ $t('table.sameStoreBrand') }}
            </el-checkbox>
            <el-checkbox
              v-model="form.is_replace_with_same_category"
              readonly
            >
              {{ $t('table.sameStoreCategory') }}
            </el-checkbox>
          </el-form-item>
          <el-form-item
            :label="$t('table.smartLinks')"
            prop="smart_links"
          >
            <editorial-smart-link-list
              :smart-links="form.smart_links"
              readonly
            />
          </el-form-item>
        </div>

        <div
          v-for="(content, count) in form.static_contents"
          :key="count"
        >
          <h4 class="title subtitle">{{ getTitleFromContentType(content.type) }}</h4>
          <div class="lang-box">

            <!-- Both locations -->
            <div class="lang-box-item">
              <el-form-item
                :label="$t('common.en')"
                :prop="getFormItemProp(content.type, 'en')">
                <el-input
                  v-if="content.singleline"
                  :value="stripTags(content.en.value)"
                  type="text"
                  autosize
                  readonly
                />
                <el-input
                  v-else
                  :value="stripTags(content.en.value)"
                  type="textarea"
                  autosize
                  readonly
                />
              </el-form-item>
            </div>

            <!-- HK location -->
            <template v-if="shouldShowFormItemForLocation(2)">
              <div class="lang-box-item">
                <el-form-item
                  :label="$t('common.zhHK')"
                  :prop="getFormItemProp(content.type, 'zh_hk')">
                  <el-input
                    v-if="content.singleline"
                    :value="stripTags(content.zh_hk.value)"
                    type="text"
                    autosize
                    readonly
                  />
                  <el-input
                    v-else
                    :value="stripTags(content.zh_hk.value)"
                    type="textarea"
                    autosize
                    readonly
                  />
                </el-form-item>
              </div>
              <div class="lang-box-item">
                <el-form-item
                  :label="$t('common.zhCN')"
                  :prop="getFormItemProp(content.type, 'zh_cn')">
                  <el-input
                    v-if="content.singleline"
                    :value="stripTags(content.zh_cn.value)"
                    type="text"
                    autosize
                    readonly
                  />
                  <el-input
                    v-else
                    :value="stripTags(content.zh_cn.value)"
                    type="textarea"
                    autosize
                    readonly
                  />

                </el-form-item>
              </div>
            </template>

            <!-- Thai location -->
            <template v-if="shouldShowFormItemForLocation(1)">
              <div class="lang-box-item">
                <el-form-item
                  :label="$t('common.th')"
                  :prop="getFormItemProp(content.type, 'th_th')">
                  <el-input
                    v-if="content.singleline"
                    :value="stripTags(content.th_th.value)"
                    type="text"
                    autosize
                    readonly
                  />
                  <el-input
                    v-else
                    :value="stripTags(content.th_th.value)"
                    type="textarea"
                    autosize
                    readonly
                  />
                </el-form-item>
              </div>
            </template>

          </div>
        </div>
      </el-form>

      <h4 class="title subtitle extra-margin">Editorial Items</h4>
      <el-table
        :span-method="handleTableSpan"
        :data="form.dynamic_contents"
        class="editorial-table"
        border
        fit
        highlight-current-row
        row-key="id">
        <el-table-column
          type="index"
          width="35"/>
        <el-table-column
          width="180"
          align="center"
          label="Type"
        >
          <template slot-scope="scope">
            <el-select
              v-model="scope.row.type"
              placeholder="Select"
              readonly
            >
              <el-option
                v-for="type in editorialTypes"
                :key="type.value"
                :label="type.label"
                :value="type.value"/>
            </el-select>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('common.en')"
          min-width="250"
          align="center">
          <template slot-scope="scope">
            <editorial-main-item
              :item-type="scope.row.type"
              :products="scope.row.products"
              v-bind.sync="scope.row.en"
              readonly
            />
          </template>
        </el-table-column>
        <el-table-column
          v-if="shouldShowFormItemForLocation(2)"
          :label="$t('common.zhHK')"
          min-width="250"
          align="center">
          <template slot-scope="scope">
            <editorial-main-item
              :item-type="scope.row.type"
              :products="scope.row.products"
              v-bind.sync="scope.row.zh_hk"
              readonly
            />
          </template>
        </el-table-column>
        <el-table-column
          v-if="shouldShowFormItemForLocation(2)"
          :label="$t('common.zhCN')"
          min-width="250"
          align="center">
          <template slot-scope="scope">
            <editorial-main-item
              :item-type="scope.row.type"
              :products="scope.row.products"
              v-bind.sync="scope.row.zh_cn"
              readonly
            />
          </template>
        </el-table-column>
        <el-table-column
          v-if="shouldShowFormItemForLocation(1)"
          :label="$t('common.th')"
          min-width="250"
          align="center">
          <template slot-scope="scope">
            <editorial-main-item
              :item-type="scope.row.type"
              :products="scope.row.products"
              v-bind.sync="scope.row.th_th"
              readonly
            />
          </template>
        </el-table-column>
      </el-table>
    </div>
  </sticky-header-layout>
</template>

<script>
import EditorialMainItem from './components/EditorialReadonlyMainItem.vue'
import EditorialSmartLinkList from './components/EditorialSmartLinkList.vue'

import EditorialsService from '../../api/editorials'
import { getRandomHash, stripTags } from '../../utils/helper'
import { EDITORIAL_ITEMS } from '../../utils/const'


export default {
  name: 'EditorialDetail',
  components: {
    EditorialMainItem,
    EditorialSmartLinkList,
  },
  props: {
    id: {
      type: [String, Number],
      default: undefined,
    },
  },
  data() {
    const validateNumber = (rule, _, callback) => {
      if (rule.field.includes('main_image_ratio')) {
        const ratioContent = this.form.static_contents.find(content => content.type === 'main_image_ratio')
        let value
        switch (rule.field) {
          case 'main_image_ratio_en':
            ({ value } = ratioContent.en)
            break
          case 'main_image_ratio_zh_hk':
            ({ value } = ratioContent.zh_hk)
            break
          case 'main_image_ratio_zh_cn':
            ({ value } = ratioContent.zh_cn)
            break
          case 'main_image_ratio_th_th':
            ({ value } = ratioContent.th_th)
            break
          default:
            break
        }

        if (!value || value === 0 || value === '0') {
          return callback(this.$t('error.thisFieldIsRequired'))
        }

        if (!+value) {
          return callback(this.$t('error.thisFieldMustBeANumber'))
        }
      }

      return callback()
    }

    const validateSpaces = (rule, _, callback) => {
      if (rule.field.includes('slug')) {
        const slugContent = this.form.static_contents.find(content => content.type === 'slug')
        let value
        switch (rule.field) {
          case 'slug_en':
            ({ value } = slugContent.en)
            break
          case 'slug_zh_hk':
            ({ value } = slugContent.zh_hk)
            break
          case 'slug_zh_cn':
            ({ value } = slugContent.zh_cn)
            break
          case 'slug_th_th':
            ({ value } = slugContent.th_th)
            break
          default:
            break
        }

        if (!value || value === 0 || value === '0') {
          return callback(this.$t('error.thisFieldIsRequired'))
        }

        if (/\s/.test(value)) {
          return callback('This field should not contain spaces.')
        }
      }
      return callback()
    }

    return {
      form: {
        title: '',
        enabled: false,
        listed: false,
        created_at: null,
        category_id: '',
        location: 2,
        gender: 'w',
        preview_token: '',
        photo_credit: '',
        u1: '',
        is_replace_with_same_store: false,
        is_replace_with_same_product: false,
        is_replace_with_same_brand: false,
        is_replace_with_same_category: false,
        smart_links: [],
        static_contents: [
          {
            type: 'title', en: { value: '' }, zh_hk: { value: '' }, zh_cn: { value: '' }, th_th: { value: '' }, singleline: '',
          },
          {
            type: 'subtitle', en: { value: '' }, zh_hk: { value: '' }, zh_cn: { value: '' }, th_th: { value: '' }, singleline: '',
          },
          {
            type: 'metatitle', en: { value: '' }, zh_hk: { value: '' }, zh_cn: { value: '' }, th_th: { value: '' }, singleline: '',
          },
          {
            type: 'metadescription', en: { value: '' }, zh_hk: { value: '' }, zh_cn: { value: '' }, th_th: { value: '' }, singleline: '',
          },
          {
            type: 'main_image', en: { value: '' }, zh_hk: { value: '' }, zh_cn: { value: '' }, th_th: { value: '' }, singleline: 'true',
          },
          {
            type: 'main_image_ratio', en: { value: '1.5' }, zh_hk: { value: '1.5' }, zh_cn: { value: '1.5' }, th_th: { value: '1.5' }, singleline: '',
          },
          {
            type: 'home_image', en: { value: '' }, zh_hk: { value: '' }, zh_cn: { value: '' }, th_th: { value: '' }, singleline: 'true',
          },
          {
            type: 'cover_image', en: { value: '' }, zh_hk: { value: '' }, zh_cn: { value: '' }, th_th: { value: '' }, singleline: 'true',
          },
          {
            type: 'author', en: { value: '' }, zh_hk: { value: '' }, zh_cn: { value: '' }, th_th: { value: '' }, singleline: '',
          },
          {
            type: 'avator', en: { value: '' }, zh_hk: { value: '' }, zh_cn: { value: '' }, th_th: { value: '' }, singleline: '',
          },
          {
            type: 'category', en: { value: '' }, zh_hk: { value: '' }, zh_cn: { value: '' }, th_th: { value: '' }, singleline: '',
          },
          {
            type: 'tag', en: { value: '' }, zh_hk: { value: '' }, zh_cn: { value: '' }, th_th: { value: '' }, singleline: '',
          },
          {
            type: 'slug', en: { value: '' }, zh_hk: { value: '' }, zh_cn: { value: '' }, th_th: { value: '' }, singleline: '',
          },
        ],
        dynamic_contents: [],
      },
      formRules: {
        title: [{ required: true, message: this.$t('error.thisFieldIsRequired'), trigger: 'blur' }],
        main_image_ratio_en: [{ validator: validateNumber, trigger: 'blur' }],
        main_image_ratio_zh_hk: [{ validator: validateNumber, trigger: 'blur' }],
        main_image_ratio_zh_cn: [{ validator: validateNumber, trigger: 'blur' }],
        main_image_ratio_th_th: [{ validator: validateNumber, trigger: 'blur' }],
        slug_en: [{ validator: validateSpaces, trigger: 'blur' }],
        slug_zh_hk: [{ validator: validateSpaces, trigger: 'blur' }],
        slug_zh_cn: [{ validator: validateSpaces, trigger: 'blur' }],
        slug_th_th: [{ validator: validateSpaces, trigger: 'blur' }],
      },
      editorialTypes: EDITORIAL_ITEMS,
      locations: [
        { value: 2, label: 'EN HK CN' },
        { value: 1, label: 'EN TH' },
      ],
      genders: [
        { value: 'w', label: 'Women' },
        { value: 'm', label: 'Men' },
        { value: 'k', label: 'Kids' },
      ],
      categories: [],
      isEdited: false,
      isLoading: false,
      isSaving: false,
      sortableObject: null,
    }
  },
  computed: {
    isEditMode() {
      return !!this.id
    },
  },
  watch: {
    form: {
      handler() {
        this.isEdited = true
      },
      deep: true,
    },
  },
  mounted() {
    if (this.isEditMode) {
      this.getEditorialDetail(this.id)
    }
  },
  beforeRouteEnter(to, from, next) {
    EditorialsService.getCategoryList().then((response) => {
      const categoryList = response.data
      const categories = [{ value: '', label: '' }];

      categoryList.forEach((category) => {
        categories.push({ value: category.id, label: category.title })
      })

      next((vm) => {
        // eslint-disable-next-line
        vm._data.categories = categories
      })
    })
  },
  beforeRouteLeave(to, from, next) {
    if (this.isSaving) {
      return next(false)
    }

    if (!this.isEdited) {
      return next()
    }

    this.$confirm(this.$t('message.leaveWithUnsavedChanges'), this.$t('common.warning'), {
      confirmButtonText: this.$t('button.leave'),
      cancelButtonText: this.$t('button.cancel'),
      type: 'warning',
    }).then(() => next()).catch(() => next(false))

    return next(false)
  },
  methods: {
    async getEditorialDetail(id) {
      this.isLoading = true
      try {
        const response = await EditorialsService.getDetail(id)
        const detail = response.data
        detail.enabled = !!+detail.enabled
        detail.listed = !!+detail.listed
        detail.category_id = detail.category.id
        detail.is_replace_with_same_store = !!+detail.is_replace_with_same_store
        detail.is_replace_with_same_product = !!+detail.is_replace_with_same_product
        detail.is_replace_with_same_brand = !!+detail.is_replace_with_same_brand
        detail.is_replace_with_same_category = !!+detail.is_replace_with_same_category

        detail.static_contents[4].singleline = 'true' // main image link will now only accept single line input
        detail.static_contents[5].singleline = 'true' // home image link will now only accept single line input
        detail.static_contents[6].singleline = 'true' // cover image link will now only accept single line input

        // for drag and drop to render items correctly
        for (let i = 0; i < detail.dynamic_contents.length; i += 1) {
          const content = detail.dynamic_contents[i]
          content.id = getRandomHash()

          switch (content.type) {
            case 'product': {
              content.type = `${content.products.length}_product`
              break
            }
            case 'text_link':
              content.en.css = JSON.parse(content.en.css)
              content.zh_hk.css = JSON.parse(content.zh_hk.css)
              content.zh_cn.css = JSON.parse(content.zh_cn.css)
              content.th_th.css = JSON.parse(content.th_th.css)

              content.en.css['font-size'] = +content.en.css['font-size'].replace('px', '')
              content.zh_hk.css['font-size'] = +content.zh_hk.css['font-size'].replace('px', '')
              content.zh_cn.css['font-size'] = +content.zh_cn.css['font-size'].replace('px', '')
              content.th_th.css['font-size'] = +content.th_th.css['font-size'].replace('px', '')
              break
            default:
              break
          }
        }
        // detail.is_replace_with_same_brand = true
        this.form = detail
        this.isLoading = false
      } catch (error) {
        this.isLoading = false
        this.$alert('Sorry, the editorial was not found!', 'Error', {
          confirmButtonText: 'OK',
          type: 'error',
        }).then(() => {
          this.goToEditorialList()
        })
      }
    },
    shouldShowFormItemForLocation(locationID) {
      return this.form.location === locationID
    },
    getTitleFromContentType(contentType) {
      const titles = {
        title: this.$t('table.title'),
        subtitle: this.$t('table.subtitle'),
        metatitle: this.$t('table.metaTitle'),
        metadescription: this.$t('table.metaDescription'),
        main_image: this.$t('table.mainImageLink'),
        main_image_ratio: this.$t('table.mainImageRatio'),
        home_image: this.$t('table.homeImage'),
        cover_image: this.$t('table.coverImage'),
        author: this.$t('table.author'),
        avator: this.$t('table.avatar'),
        category: this.$t('table.category'),
        tag: this.$t('table.tag'),
        slug: this.$t('table.slug'),
      }
      return titles[contentType]
    },
    getFormItemProp(contentType, locale) {
      return `${contentType}_${locale}`
    },
    handleTableSpan({ row, columnIndex }) {
      // For type with product, we don't have different languages, so just let the users to enter the content one time.
      // Check detail: http://element.eleme.io/#/en-US/component/table
      if (row.type.includes('product')) {
        switch (this.form.location) {
          case 1:
            if (columnIndex > 1 && columnIndex < 4) {
              if (columnIndex === 2) {
                return [1, 2]
              }
              return [0, 0]
            }
            break
          case 2:
            if (columnIndex > 1 && columnIndex < 5) {
              if (columnIndex === 2) {
                return [1, 3]
              }
              return [0, 0]
            }
            break
          default:
            break
        }
      }
      return undefined
    },
    goToEditorialList() {
      this.$router.push({ path: '/editorial/list' })
    },
    handleCancel() {
      this.goToEditorialList()
    },
    stripTags,
  },
}
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/view-base.scss";
@import "~@/assets/styles/table.scss";
@import "~@/assets/styles/form.scss";
.all-language-button{
  display: block !important;
  margin-left: auto !important;
  font-size: 10px !important;
}
.editorial-table {
  border-radius: 5px;
}
.tags-container {
  width: 100%;
  .el-tag + .el-tag {
    margin-left: 10px;
  }
}
.space-between-container {
  display: flex;
  justify-content: space-between;
  align-content: center;
}
.subtitle {
  margin: 10px auto;
}
.extra-margin {
  margin-top: 30px;
}

::v-deep {
  .el-input.is-disabled .el-input__inner,
  .el-textarea.is-disabled .el-textarea__inner {
    background-color: unset;
    color: #888;
  }
}

</style>
